import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Coins, Rocket } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { authApi } from "./authApi";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, active = true;
    const poll = async () => {
      if (!active) return;
      try {
        const r = await authApi.paymentStatus(sessionId);
        if (r.payment_status === "paid") { setInfo(r); setState("paid"); return; }
        if (["failed", "expired"].includes(r.payment_status)) { setState("error"); return; }
      } catch (_) {}
      if (++tries >= 12) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <PlatformLayout searchDock={false} solidHeader>
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-24 pb-16" data-testid="payment-success">
        <div className="max-w-md w-full text-center border border-white/10 bg-[#0d0d10] p-8">
          {state === "checking" && (<><Loader2 className="w-12 h-12 text-[#C19A6B] mx-auto mb-4 animate-spin" /><h1 className="text-xl font-light text-white">Validation du paiement…</h1><p className="text-sm text-zinc-400 mt-2">Merci de patienter quelques secondes.</p></>)}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-green-400 mx-auto mb-4" />
              <h1 className="text-2xl font-light text-white">Paiement confirmé 🎉</h1>
              {info?.kind === "subscription" ? (
                <p className="text-sm text-zinc-400 mt-2 inline-flex items-center gap-1.5 justify-center" data-testid="sub-success-msg"><Rocket className="w-4 h-4 text-[#C19A6B]" /> Abonnement Site personnalisé activé. Vous pouvez désormais mettre votre site en ligne et configurer votre domaine.</p>
              ) : info?.kind === "boost" ? (
                <p className="text-sm text-zinc-400 mt-2 inline-flex items-center gap-1.5 justify-center" data-testid="boost-success-msg"><Rocket className="w-4 h-4 text-[#C19A6B]" /> Votre annonce est boostée pour {info?.days} jours : mise en tête du matching réseau et de la recherche, avec le badge « Boosté ».</p>
              ) : (
                <p className="text-sm text-zinc-400 mt-2 inline-flex items-center gap-1.5 justify-center"><Coins className="w-4 h-4 text-[#C19A6B]" /> {info?.credits} crédit(s) ajouté(s) à votre portefeuille.</p>
              )}
              {info?.auto_published && <p className="text-sm text-green-400 mt-2 inline-flex items-center gap-1.5 justify-center"><Rocket className="w-4 h-4" /> Votre annonce a été publiée automatiquement.</p>}
              <Link to="/mon-compte" data-testid="back-to-dashboard" className="mt-6 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Retour à mon espace</Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
              <h1 className="text-xl font-light text-white">Paiement non confirmé</h1>
              <p className="text-sm text-zinc-400 mt-2">Si le montant a été débité, vos crédits apparaîtront sous peu. Sinon, réessayez depuis votre espace.</p>
              <Link to="/mon-compte" className="mt-6 inline-flex items-center gap-2 border border-white/15 text-zinc-200 hover:border-[#C19A6B] hover:text-[#C19A6B] uppercase tracking-wider text-sm px-6 py-3 transition-colors">Retour à mon espace</Link>
            </>
          )}
        </div>
      </div>
    </PlatformLayout>
  );
}

export function PaymentCancel() {
  return (
    <PlatformLayout searchDock={false} solidHeader>
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-24 pb-16" data-testid="payment-cancel">
        <div className="max-w-md w-full text-center border border-white/10 bg-[#0d0d10] p-8">
          <XCircle className="w-14 h-14 text-zinc-400 mx-auto mb-4" />
          <h1 className="text-xl font-light text-white">Paiement annulé</h1>
          <p className="text-sm text-zinc-400 mt-2">Aucun montant n'a été débité. Vous pouvez réessayer à tout moment.</p>
          <Link to="/mon-compte" data-testid="cancel-back" className="mt-6 inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Retour à mon espace</Link>
        </div>
      </div>
    </PlatformLayout>
  );
}
